import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { joyApi } from "../api/joyApi";
import { BADGES, isBadgeUnlocked, levelInfo } from "../lib/badges";
import AppShell from "../components/AppShell";
import TopBar from "../components/TopBar";
import JoyButton from "../components/JoyButton";
import LoadingScreen from "../components/LoadingScreen";
import ErrorState from "../components/ErrorState";

function StatBox({ emoji, value, label, bg, testId }) {
  return (
    <div data-testid={testId} className="flex flex-col items-center rounded-[20px] py-4 border-2 border-white" style={{ background: bg }}>
      <span className="text-2xl">{emoji}</span>
      <span className="font-black text-[#4A3728] text-2xl mt-1">{value}</span>
      <span className="text-xs font-bold text-[#8B7355] uppercase">{label}</span>
    </div>
  );
}

export default function Progress() {
  const nav = useNavigate();
  const { userId } = useAuth();
  const { data: progress, isLoading, isError, refetch } = useQuery({
    queryKey: ["progress", userId],
    queryFn: () => joyApi.progress(userId),
    enabled: !!userId,
  });

  if (isLoading) return <LoadingScreen />;
  if (isError)
    return (
      <AppShell>
        <TopBar title="Meu Progresso 🌟" to="/" />
        <ErrorState onRetry={refetch} />
      </AppShell>
    );

  const li = levelInfo(progress);
  const completed = progress?.completed_lessons?.length || 0;

  return (
    <AppShell>
      <TopBar title="Meu Progresso 🌟" to="/" testId="progress-topbar" />

      <div className="flex flex-col items-center joy-slide-in">
        <div className="relative w-32 h-32">
          <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
            <circle cx="60" cy="60" r="52" fill="none" stroke="#FFE0EC" strokeWidth="12" />
            <circle
              cx="60" cy="60" r="52" fill="none" stroke="#FF69B4" strokeWidth="12" strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 52}`}
              strokeDashoffset={`${2 * Math.PI * 52 * (1 - li.percent / 100)}`}
              style={{ transition: "stroke-dashoffset 0.8s ease" }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-3xl">🏆</span>
            <span className="font-black text-[#4A3728] text-lg leading-none">Nível {li.level}</span>
          </div>
        </div>
        <p className="font-bold text-[#8B7355] mt-3">
          {li.inLevel} / {li.needed} XP para o Nível {li.level + 1}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-6">
        <StatBox emoji="⭐" value={progress?.total_xp || 0} label="XP" bg="#FFF0C4" testId="progress-xp" />
        <StatBox emoji="🔥" value={progress?.streak_days || 0} label="Dias" bg="#FFE0EC" testId="progress-streak" />
        <StatBox emoji="💎" value={progress?.coins || 0} label="Moedas" bg="#DCF3F0" testId="progress-coins" />
        <StatBox emoji="📚" value={completed} label="Lições" bg="#DCEAF5" testId="progress-lessons" />
      </div>

      <h2 className="font-black text-lg text-[#4A3728] mt-7 mb-3">🏆 Conquistas</h2>
      <div className="grid grid-cols-3 gap-3" data-testid="badges-grid">
        {BADGES.map((b) => {
          const unlocked = isBadgeUnlocked(b, progress);
          return (
            <div
              key={b.id}
              data-testid={`badge-${b.id}-${unlocked ? "unlocked" : "locked"}`}
              className={`flex flex-col items-center text-center rounded-[18px] p-3 border-2 ${
                unlocked ? "bg-white border-[#FFD700]" : "bg-[#F3ECDD] border-[#E7DCC4] opacity-60"
              }`}
            >
              <span className={`text-3xl ${unlocked ? "" : "grayscale"}`}>{unlocked ? b.emoji : "🔒"}</span>
              <span className="font-black text-[10px] text-[#4A3728] leading-tight mt-1">{b.name}</span>
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-[22px] p-4 flex items-center gap-3 border-2 border-[#FFE0EC] mt-6">
        <img src="/joy.png" alt="Joy" className="w-12 h-12 rounded-full object-cover border-2 border-[#FF69B4]" />
        <p className="font-black text-[#4A3728]">🦊 Continue assim! 💕</p>
      </div>

      <JoyButton variant="pink" full className="mt-5" data-testid="continue-learning" onClick={() => nav("/lessons")}>
        ▶️ Continuar Aprendendo 🌟
      </JoyButton>
    </AppShell>
  );
}
